import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export interface HolidayConfig {
  dates: string[];
  vacationMonths: { year: number; month: number }[];
  vacationRanges: { year: number; month: number; from: number; to: number }[];
}

let cachedHolidayConfigPromise: Promise<HolidayConfig> | null = null;

export function getHolidayConfig(): Promise<HolidayConfig> {
  if (!cachedHolidayConfigPromise) {
    cachedHolidayConfigPromise = getDoc(doc(db, 'appConfig', 'holidays')).then(snap => {
      return snap.exists()
        ? { dates: snap.data().dates ?? [], vacationMonths: snap.data().vacationMonths ?? [], vacationRanges: snap.data().vacationRanges ?? [] }
        : { dates: [], vacationMonths: [], vacationRanges: [] };
    });
  }
  return cachedHolidayConfigPromise;
}

function todayIST(): string {
  const now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function checkHoliday(dateKey: string, config: HolidayConfig): boolean {
  const y = parseInt(dateKey.slice(0, 4), 10);
  const m = parseInt(dateKey.slice(4, 6), 10);
  const d = parseInt(dateKey.slice(6, 8), 10);
  const day = new Date(y, m - 1, d).getDay();
  if (day === 0 || day === 6) return true;
  if (config.dates.includes(dateKey)) return true;
  if (config.vacationMonths.some(v => v.year === y && v.month === m)) return true;
  if (config.vacationRanges.some(v => v.year === y && v.month === m && d >= v.from && d <= v.to)) return true;
  return false;
}

export function useIsHoliday(): boolean | null {
  const [isHoliday, setIsHoliday] = useState<boolean | null>(null);

  useEffect(() => {
    const dateKey = todayIST();
    getHolidayConfig().then(config => {
      setIsHoliday(checkHoliday(dateKey, config));
    }).catch(() => setIsHoliday(null));
  }, []);

  return isHoliday;
}
